import ConfirmationFactory from '@/common/services/ConfirmationFactory';
import AccessInheritanceConfirmation from '@/drive/components/AccessInheritanceConfirmation/AccessInheritanceConfirmation';
import {
  AccessInheritanceContent,
  AccessInheritanceDestination,
  AccessInheritanceGroup,
  AccessInheritanceSummary,
  getAccessInheritanceContent,
} from '@/drive/components/AccessInheritanceConfirmation/AccessInheritanceConfirmation.constants';
import { CollectionTreeItem } from '@/drive/entities/DriveTreeItem/CollectionTreeItem';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { AccessInheritanceDecision } from '@/drive/types/AccessInheritanceDecision';
import { AccessInheritanceSubject } from '@/drive/types/AccessInheritanceSubject';
import { getAccessById } from '@/security/constants/access';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { AccessTargetType } from '@/security/types/AccessTarget';

const CONTAINER_TARGET_TYPES: AccessTargetType[] = ['project', 'driveProject', 'collection', 'driveFolder'];

export async function resolveAccessInheritance(
  subjects: AccessInheritanceSubject[],
  destination: DriveTreeItem,
): Promise<AccessInheritanceDecision> {
  if (destination.getAccess() === GlobalAccessLevel.Restricted) {
    return AccessInheritanceDecision.LeaveUnchanged;
  }

  const hiddenSubjects = subjects.filter((subject) => subject.access !== GlobalAccessLevel.Public);

  if (hiddenSubjects.length === 0) {
    return AccessInheritanceDecision.LeaveUnchanged;
  }

  const ownSubjects = hiddenSubjects.filter((subject) => subject.canManageAccess);

  const destinationParams: AccessInheritanceDestination = {
    typeLabel: destination instanceof CollectionTreeItem ? 'collection' : 'folder',
  };

  const foreignSubjects = hiddenSubjects.filter((subject) => !subject.canManageAccess);

  const summary: AccessInheritanceSummary = {
    publicCount: subjects.length - hiddenSubjects.length,
    own: toGroup(ownSubjects),
    foreign: toGroup(foreignSubjects),
    hasOwnContainers: ownSubjects.some(isContainerSubject),
  };

  return requestAccessInheritanceDecision(getAccessInheritanceContent(destinationParams, summary));
}

async function requestAccessInheritanceDecision(content: AccessInheritanceContent): Promise<AccessInheritanceDecision> {
  try {
    return await ConfirmationFactory.generate<AccessInheritanceDecision>(({ show, proceed, dismiss }) => (
      <AccessInheritanceConfirmation
        content={content}
        isShowed={show}
        onSelect={(decision) => proceed(decision)}
        onClose={dismiss}
      />
    ));
  } catch {
    return AccessInheritanceDecision.Cancel;
  }
}

function toGroup(subjects: AccessInheritanceSubject[]): AccessInheritanceGroup {
  const accessTitles = subjects
    .map(getAccessTitle)
    .filter((accessTitle): accessTitle is string => accessTitle !== null);

  return {
    count: subjects.length,
    accessTitles: [...new Set(accessTitles)],
  };
}

function getAccessTitle(subject: AccessInheritanceSubject): Nullable<string> {
  return getAccessById(subject.access, subject.targetType)?.title.toLowerCase() ?? null;
}

function isContainerSubject(subject: AccessInheritanceSubject): boolean {
  return CONTAINER_TARGET_TYPES.includes(subject.targetType);
}
